import { axiosClient } from "../AxiosClient";

const BY_ROLE_PATH = "/v1/step-connections/by-role";
const ASSIGNMENTS_BY_FLOW_PATH = "/v1/flow-segmentation-rules/by-flow";
const CATALOG_PATH = "/v1/flow-segmentation-rules/catalog";

export const isCreateAssignmentApiReady = true;

const ASSIGNMENTS_POST_PATH = "/v1/flow-segmentation-rules/assignments";

const VALID_FLOW_ROLES = new Set(["PRIMARY", "SECONDARY"]);

const LIST_KEYS = [
  "content",
  "data",
  "items",
  "results",
  "stepConnections",
  "step_connections",
];

/**
 * @param {unknown} payload
 * @returns {unknown[]}
 */
const unwrapListPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    for (const key of LIST_KEYS) {
      const nested = payload[key];
      if (Array.isArray(nested)) return nested;
    }
  }
  return [];
};

/**
 * @param {Record<string, unknown>} item
 * @returns {{ uuid: string, name?: string | null, description?: string | null }}
 */
const normalizeStepConnectionSummary = (item) => {
  const uuid =
    item.uuid ??
    item.stepConnectionUuid ??
    item.step_connection_uuid ??
    item.id;
  return {
    uuid: uuid != null ? String(uuid) : "",
    name: (item.name ?? item.flowName ?? item.nombre ?? null),
    description: (item.description ?? item.descripcion ?? null),
  };
};

/**
 * @param {Record<string, unknown>} item
 * @returns {{ idAsignacion: number, asignaciones: string }}
 */
const normalizeAssignmentLabel = (item) => ({
  idAsignacion: Number(
    item.idAsignacion ?? item.id_asignacion ?? item.id ?? 0
  ),
  asignaciones: String(
    item.asignaciones ?? item.assignments ?? item.label ?? ""
  ),
});

/**
 * @param {string} flowRole
 * @returns {Promise<{ uuid: string, name?: string | null, description?: string | null }[]>}
 */
export const listStepConnectionsByFlowRole = async (flowRole) => {
  if (!VALID_FLOW_ROLES.has(flowRole)) {
    throw new Error("Tipo de flujo inválido. Use PRIMARY o SECONDARY.");
  }
  const { data } = await axiosClient.get(`${BY_ROLE_PATH}/${flowRole}`);
  return unwrapListPayload(data)
    .map((item) => normalizeStepConnectionSummary(item))
    .filter((item) => item.uuid !== "");
};

/**
 * @param {string} stepConnectionUuid
 * @returns {Promise<{ idAsignacion: number, asignaciones: string }[]>}
 */
export const listFlowSegmentationAssignments = async (stepConnectionUuid) => {
  const { data } = await axiosClient.get(
    `${ASSIGNMENTS_BY_FLOW_PATH}/${stepConnectionUuid}/assignments`
  );
  return unwrapListPayload(data).map((item) => normalizeAssignmentLabel(item));
};

/**
 * @param {string} stepConnectionUuid
 * @param {number} idAsignacion
 * @returns {Promise<void>}
 */
export const deleteFlowSegmentationAssignment = async (stepConnectionUuid, idAsignacion) => {
  await axiosClient.delete(
    `${ASSIGNMENTS_BY_FLOW_PATH}/${stepConnectionUuid}/${idAsignacion}`
  );
};

/**
 * @typedef {{ uuid: string, name: string }} CommerceOption
 * @typedef {{ commerces: CommerceOption[], typeClient: string[] }} SegmentationFormCatalog
 */

/**
 * @returns {Promise<SegmentationFormCatalog>}
 */
export const getSegmentationFormCatalog = async () => {
  const { data } = await axiosClient.get(CATALOG_PATH);
  const commerces = Array.isArray(data?.commerces)
    ? data.commerces
        .map((c) => ({
          uuid: String(c.uuid ?? c.id ?? ""),
          name: String(c.name ?? c.nombre ?? ""),
        }))
        .filter((c) => c.uuid !== "")
    : [];
  const typeClient = Array.isArray(data?.typeClient)
    ? data.typeClient.map((t) => String(t))
    : [];
  return { commerces, typeClient };
};

/**
 * @param {{
 *   stepConnectionUuid: string,
 *   commerces: string[],
 *   typeClient: string[],
 *   scoreMin?: number,
 *   scoreMax?: number,
 * }} payload
 * @returns {Promise<void>}
 */
export const createFlowSegmentationAssignment = async ({
  stepConnectionUuid,
  commerces,
  typeClient,
  scoreMin,
  scoreMax,
}) => {
  const body = {
    stepConnectionUuid,
    commerces: commerces ?? [],
    typeClient: typeClient ?? [],
  };
  if (scoreMin != null && scoreMax != null) {
    body.scoreMin = scoreMin;
    body.scoreMax = scoreMax;
  }
  await axiosClient.post(ASSIGNMENTS_POST_PATH, body);
};
